import { createTheme } from "@mui/material/styles";

// Modern light theme
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2563eb", // Modern blue
      light: "#4b83ff",
      dark: "#1e40af",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#6366f1", // Indigo
      light: "#818cf8",
      dark: "#4f46e5",
      contrastText: "#ffffff",
    },
    error: {
      main: "#ef4444", // Modern red
      light: "#f87171",
      dark: "#dc2626",
    },
    warning: {
      main: "#f59e0b", // Modern amber
      light: "#fbbf24",
      dark: "#d97706",
    },
    info: {
      main: "#0ea5e9", // Modern sky blue
      light: "#38bdf8",
      dark: "#0284c7",
    },
    success: {
      main: "#10b981", // Modern emerald
      light: "#34d399",
      dark: "#059669",
    },
    background: {
      default: "#f9fafb", // Very light gray
      paper: "#ffffff",
    },
    text: {
      primary: "#111827", // Very dark gray
      secondary: "#4b5563", // Mid gray
      disabled: "#9ca3af", // Light gray
    },
    // Custom colors for enhanced UI
    custom: {
      navyBlue: "#1e3a8a", // Navy blue for hover states
      lightNavy: "#3b82f6", // Light navy blue
      gradientStart: "#667eea",
      gradientEnd: "#764ba2",
      loadingBg: "#f8fafc",
      cardShadow: "rgba(0, 0, 0, 0.05)",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.5,
    },
    button: {
      textTransform: "none", // No uppercase text in buttons
      fontWeight: 500,
    },
    // Custom typography variants for enhanced UI
    loading: {
      fontSize: "1.1rem",
      fontWeight: 500,
      color: "#4b5563",
    },
    loadingTitle: {
      fontSize: "1.5rem",
      fontWeight: 600,
      color: "#111827",
    },
  },
  shape: {
    borderRadius: 8, // Slightly rounded corners
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none", // Remove shadows
          borderRadius: 12,
          padding: "10px 24px",
          fontSize: "1rem",
          fontWeight: 600,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            transform: "translateY(-2px)",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)", // Navy blue gradient
            boxShadow: "0 8px 16px rgba(30, 58, 138, 0.3)",
          },
        },
        containedSecondary: {
          "&:hover": {
            backgroundColor: "#1e3a8a", // Navy blue
          },
        },
        outlinedPrimary: {
          borderColor: "#2563eb",
          "&:hover": {
            borderColor: "#1e3a8a", // Navy blue
            backgroundColor: "rgba(30, 58, 138, 0.04)",
          },
        },
        textPrimary: {
          "&:hover": {
            backgroundColor: "rgba(30, 58, 138, 0.04)", // Navy blue with transparency
          },
        },
      },
    },
    // MuiPaper: {
    //   styleOverrides: {
    //     root: {
    //       boxShadow:
    //         "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)", // Lighter shadow
    //     },
    //     outlined: {
    //       borderColor: "#e5e7eb", // Light gray border
    //     },
    //   },
    // },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
              borderColor: "#1e3a8a", // Navy blue on hover
            },
            "&.Mui-focused fieldset": {
              borderColor: "#1e3a8a", // Navy blue when focused
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          borderRadius: 16, // More rounded cards
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow:
              "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            transform: "translateY(-2px)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #e5e7eb", // Light gray border
        },
        head: {
          fontWeight: 600, // Bolder table headers
          backgroundColor: "#f9fafb", // Very light gray background
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: "#1e3a8a", // Navy blue loading spinner
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: "#e5e7eb",
          borderRadius: 4,
          height: 6,
        },
        bar: {
          backgroundColor: "#1e3a8a", // Navy blue progress bar
          borderRadius: 4,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(30, 58, 138, 0.04)", // Navy blue with transparency
          },
        },
      },
    },
  },
});

export default theme;
